import type {
  DealHealthScoreRootCauseDetailsState,
  UseDealHealthScoreRootCauseDetails,
} from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import { extractDealHealthScoreRootCauseDetails } from '@openx/components-deal/lib/utils/dealHealthScoreRootCauseUtils.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { GetDealHealthScoreRootCausesDocument } from '../../../graphql/query/deals/GetDealHealthScoreRootCauses.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types.ts';

const defaultDetails: DealHealthScoreRootCauseDetailsState = {
  aiAnalysis: null,
  currentScore: null,
  error: false,
  loading: false,
};

export const useDealHealthScoreRootCauseDetails: UseDealHealthScoreRootCauseDetails = () => {
  const queryClient = useQueryClient();
  const [detailsByDealId, setDetailsByDealId] = useState<Record<string, DealHealthScoreRootCauseDetailsState>>({});

  const getDealRootCauseDetails = useCallback(
    (dealId: string) => detailsByDealId[dealId] ?? defaultDetails,
    [detailsByDealId]
  );

  const loadDealRootCauseDetails = useCallback(
    async (dealId: string, options?: { force?: boolean }) => {
      if (!dealId) return;

      const current = detailsByDealId[dealId] ?? defaultDetails;
      if (current.loading) return;

      if (!options?.force) {
        const hasCachedResult = Boolean(current.aiAnalysis || current.error);
        if (hasCachedResult) return;
      }

      setDetailsByDealId(previous => ({
        ...previous,
        [dealId]: { aiAnalysis: null, currentScore: null, error: false, loading: true },
      }));

      try {
        const data = await getGraphQLRequestFromClient(queryClient)(GetDealHealthScoreRootCausesDocument, {
          dealIds: [dealId],
        });
        const { aiAnalysis, currentScore } = extractDealHealthScoreRootCauseDetails(
          data.dealHealthScoreRootCauses,
          dealId
        );

        setDetailsByDealId(previous => ({
          ...previous,
          [dealId]: {
            aiAnalysis: aiAnalysis ?? null,
            currentScore: currentScore ?? null,
            error: !aiAnalysis,
            loading: false,
          },
        }));
      } catch {
        setDetailsByDealId(previous => ({
          ...previous,
          [dealId]: { aiAnalysis: null, currentScore: null, error: true, loading: false },
        }));
      }
    },
    [detailsByDealId, queryClient]
  );

  const reloadDealRootCauseDetails = useCallback(
    (dealId: string) => loadDealRootCauseDetails(dealId, { force: true }),
    [loadDealRootCauseDetails]
  );

  return useMemo(
    () => ({
      getDealRootCauseDetails,
      loadDealRootCauseDetails,
      reloadDealRootCauseDetails,
    }),
    [getDealRootCauseDetails, loadDealRootCauseDetails, reloadDealRootCauseDetails]
  );
};
